/* eslint-disable @typescript-eslint/ban-types */

import './DataTable.css';

import { Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronSort, ChevronUp, Renew } from '@carbon/icons-react';
import {
    ColumnDef,
    RowData,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import React, { Dispatch, ReactElement, ReactNode, SetStateAction, useEffect, useState } from 'react';

import Collapse from './Collapse';

export type ColumnBreakpoints = { [column: string]: 'sm' | 'md' | 'lg' | 'xl' | '2xl' };

interface DataTableProps<T extends Object> {
    data: T[];
    setData?: (data: T[] | ((oldData: T[]) => T[])) => void;
    columns: ColumnDef<T>[];
    expandedContent?(data: T, isExpanded: boolean): React.ReactNode;
    expandedContentColOffset?: number; // Number of data columns to skip before showing expanded content (default: 0)
    loading: boolean;
    fetchData?: (pageSize: number, pageIndex: number) => void;
    refreshData?: () => void;
    onRecordClick?: (record: T) => void;
    recordsAreClickable?: boolean;
    label?: string;
    hasTopRadius?: boolean;
    columnBreakpoints?: ColumnBreakpoints;
    recordKey?: keyof T;
    selectedRecords?: T[];
    setSelectedRecords?: Dispatch<SetStateAction<T[]>>;
    shouldShowLargePageSizes?: boolean;
}

declare module '@tanstack/table-core' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface TableMeta<TData extends RowData> {
        updateData?: (rowIndex: number, columnId: string, value: unknown) => void;
    }
}

function DataTable<T extends Object>(props: DataTableProps<T>): ReactElement {
    const {
        data,
        setData,
        columns,
        expandedContent,
        expandedContentColOffset = 0,
        loading,
        fetchData,
        refreshData,
        onRecordClick,
        recordsAreClickable,
        label = 'Records',
        hasTopRadius = true,
        columnBreakpoints,
        recordKey,
        selectedRecords,
        setSelectedRecords,
        shouldShowLargePageSizes,
    } = props;
    const updateData = (rowIndex: number, columnId: string, value: unknown): void => {
        setData &&
            setData((old: T[]) =>
                old.map((row, index) => {
                    if (index === rowIndex) {
                        return {
                            ...old[rowIndex],
                            [columnId]: value,
                        };
                    }
                    return row;
                })
            );
    };

    const table = useReactTable({
        columns: columns,
        data: data,
        enableMultiSort: false,
        autoResetAll: true,
        enableExpanding: true,
        enableRowSelection: !!setSelectedRecords,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        meta: {
            updateData,
        },
    });

    const [paginationButtons, setPaginationButtons] = useState<ReactNode[]>([]);

    const getTableCellClass = (columnName: string): string =>
        columnBreakpoints?.[columnName] ? `hidden ${columnBreakpoints[columnName]}:table-cell` : '';

    useEffect(() => {
        fetchData && fetchData(table.getState().pagination.pageSize, table.getState().pagination.pageIndex);
    }, [fetchData, table]);

    // Extracting pagination state into variable to be used in hook dependency array
    const paginationState = table.getState().pagination;
    useEffect(() => {
        const pageButtons: ReactNode[] = [];

        if (paginationState.pageIndex > 2) {
            pageButtons.push(
                <Pagination.Item
                    onClick={() => {
                        table.setPageIndex(0);
                    }}
                    active={paginationState.pageIndex === 0}
                    key="first-page-button"
                >
                    {1}
                </Pagination.Item>
            );
        }
        if (paginationState.pageIndex > 3) {
            pageButtons.push(<Pagination.Ellipsis key="pagination-ellipsis-beginning" disabled />);
        }

        const totalPageCount = table.getPageCount();
        for (let i = 0; i < totalPageCount; i++) {
            if (Math.abs(i - paginationState.pageIndex) <= 2) {
                pageButtons.push(
                    <Pagination.Item
                        onClick={() => {
                            table.setPageIndex(i);
                        }}
                        active={paginationState.pageIndex === i}
                        key={'page-button-' + i}
                        data-testid={`page-button-${i}`}
                    >
                        {i + 1}
                    </Pagination.Item>
                );
            }
        }

        if (totalPageCount - 1 - paginationState.pageIndex > 3) {
            pageButtons.push(<Pagination.Ellipsis key="pagination-ellipsis-end" disabled />);
        }
        if (totalPageCount - 1 - paginationState.pageIndex > 2) {
            pageButtons.push(
                <Pagination.Item
                    onClick={() => {
                        table.setPageIndex(totalPageCount - 1);
                    }}
                    active={paginationState.pageIndex === totalPageCount - 1}
                    key="last-page-button"
                >
                    {totalPageCount}
                </Pagination.Item>
            );
        }

        setPaginationButtons(pageButtons);
    }, [paginationState.pageIndex, table, data, paginationState.pageSize]);

    // Maintains state of selected records when filtering
    useEffect(() => {
        if (recordKey && selectedRecords) {
            if(selectedRecords?.length === 0){
                table.toggleAllRowsSelected(false);
            }
            selectedRecords.forEach((selectedRecord) => {
                const foundRow = table
                    .getRowModel()
                    .rows.find((row) => row.original[recordKey] === selectedRecord[recordKey]);

                if (foundRow) {
                    foundRow.toggleSelected(true);
                }
            });
        }
    }, [recordKey, selectedRecords, table]);

    return (
        <div className={`${hasTopRadius ? 'table-container' : 'table-container-without-top-radius'} ${data.length === 0 ? 'empty-table' : ''}`}>
            <Table>
                <thead>
                    <tr>
                        {/* When expandedContent exists: checkbox and expander/refresh are separate columns */}
                        {selectedRecords && setSelectedRecords && recordKey && expandedContent && <th />}
                        {/* Shared column for refresh (header) / checkbox (rows) when no expandedContent */}
                        {/* OR separate column for expander/refresh when expandedContent exists */}
                        {(refreshData || expandedContent || selectedRecords) && (
                            <th>
                                {refreshData && (
                                    <Button
                                        variant="link"
                                        title="Refresh table data"
                                        onClick={refreshData}
                                        data-testid="refreshTableButton"
                                    >
                                        <Renew size={24} />
                                    </Button>
                                )}
                            </th>
                        )}
                        {table.getFlatHeaders().map((header) => (
                            <th
                                key={'header-' + header.id}
                                className={`${getTableCellClass(header.id)}${
                                    header.column.getCanSort() ? ' clickable' : ' unset-width'
                                }`}
                                onClick={header.column.getToggleSortingHandler()}
                                data-testid={`header-${header.id}`}
                            >
                                {header.column.getCanSort() && (
                                    <>
                                        {{
                                            asc: <ChevronUp size={24} className="sort-icon" />,
                                            desc: <ChevronDown size={24} className="sort-icon" />,
                                        }[header.column.getIsSorted() as string] ?? (
                                            <ChevronSort size={24} className="sort-icon" />
                                        )}
                                    </>
                                )}
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                        ))}
                    </tr>
                </thead>
                {!loading && data.length ? (
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <React.Fragment key={'row-' + row.index}>
                                    <tr
                                        onClick={(_event: React.MouseEvent) => {
                                            if (onRecordClick) {
                                                onRecordClick(row.original);
                                            }
                                        }}
                                        className={`${recordsAreClickable ? 'navigable-row' : ''} ${
                                            expandedContent ? 'expanded-row' : ''
                                        }`}
                                        data-testid={`row-${row.index}`}
                                    >
                                        {/* When expandedContent exists: checkbox is separate column */}
                                        {selectedRecords && setSelectedRecords && recordKey && expandedContent && (
                                            <td>
                                                <input
                                                    title="rowCheckbox"
                                                    className="row-selection"
                                                    type="checkbox"
                                                    checked={row.getIsSelected()}
                                                    onChange={() => {
                                                        if (!row.getIsSelected()) {
                                                            setSelectedRecords((prev) => {
                                                                return [...prev, row.original];
                                                            });
                                                        } else {
                                                            setSelectedRecords((prev) => {
                                                                return prev.filter(
                                                                    (record) =>
                                                                        record[recordKey] !== row.original[recordKey]
                                                                );
                                                            });
                                                        }

                                                        row.toggleSelected();
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    id={`row-checkbox-${row.index}`}
                                                    data-testid={`rowCheckbox-${row.index}`}
                                                />
                                            </td>
                                        )}
                                        {/* Shared column: checkbox (when no expandedContent) OR chevron (when expandedContent exists) */}
                                        {(refreshData || expandedContent || selectedRecords) && (
                                            <td>
                                                {/* Show checkbox when no expandedContent */}
                                                {!expandedContent && selectedRecords && setSelectedRecords && recordKey && (
                                                    <input
                                                        title="rowCheckbox"
                                                        className="row-selection"
                                                        type="checkbox"
                                                        checked={row.getIsSelected()}
                                                        onChange={() => {
                                                            if (!row.getIsSelected()) {
                                                                setSelectedRecords((prev) => {
                                                                    return [...prev, row.original];
                                                                });
                                                            } else {
                                                                setSelectedRecords((prev) => {
                                                                    return prev.filter(
                                                                        (record) =>
                                                                            record[recordKey] !== row.original[recordKey]
                                                                    );
                                                                });
                                                            }

                                                            row.toggleSelected();
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                        }}
                                                        id={`row-checkbox-${row.index}`}
                                                        data-testid={`rowCheckbox-${row.index}`}
                                                    />
                                                )}
                                                {/* Show chevron when expandedContent exists */}
                                                {expandedContent && (
                                                    <Button
                                                        variant="link"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            row.toggleExpanded();
                                                        }}
                                                        className="p-10 chevron-icon"
                                                        style={{ minWidth: 'auto' }}
                                                    >
                                                        {row.getIsExpanded() ? (
                                                            <ChevronDown size={20} />
                                                        ) : (
                                                            <ChevronRight size={20} />
                                                        )}
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={'cell-' + cell.column.columnDef.header + '-' + row.id}
                                                    className={getTableCellClass(cell.column.id)}
                                                    data-testid={`cell-${cell.column.id}-${row.index}`}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {expandedContent && row.getIsExpanded() ? (
                                        <tr
                                            className="expanded-row"
                                            data-testid={`expanded-row-${row.index}`}
                                        >
                                            {/* Empty cell for checkbox column (only when expandedContent exists) */}
                                            {selectedRecords && setSelectedRecords && recordKey && expandedContent && <td />}
                                            {/* Empty cell for shared/expander column */}
                                            {(refreshData || expandedContent || selectedRecords) && <td />}
                                            {/* Empty cells for leading data columns based on offset */}
                                            {Array.from({ length: expandedContentColOffset }).map((_, index) => (
                                                <td key={`offset-${index}`} />
                                            ))}
                                            {/* Content cell spanning remaining columns */}
                                            <td
                                                colSpan={table.getVisibleFlatColumns().length - expandedContentColOffset}
                                                style={{ padding: '0px' }}
                                            >
                                                <Collapse isOpen={row.getIsExpanded()}>
                                                    {expandedContent(row.original, row.getIsExpanded())}
                                                </Collapse>
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                ) : (
                    <tbody>
                        <tr className="table__empty-row">
                            {loading ? (
                                <td
                                    colSpan={
                                        table.getFlatHeaders().length +
                                        (selectedRecords && setSelectedRecords && recordKey && expandedContent ? 1 : 0) +
                                        (refreshData || expandedContent || selectedRecords ? 1 : 0)
                                    }
                                    className="spinner-container"
                                    data-testid="loading-spinner"
                                >
                                    <Spinner className="table-spinner" variant="primary" size="sm" animation="border" />
                                </td>
                            ) : (
                                <td
                                    colSpan={
                                        table.getFlatHeaders().length +
                                        (selectedRecords && setSelectedRecords && recordKey && expandedContent ? 1 : 0) +
                                        (refreshData || expandedContent || selectedRecords ? 1 : 0)
                                    }
                                    className="table__empty-text"
                                >
                                    No {label} Found
                                </td>
                            )}
                        </tr>
                    </tbody>
                )}
            </Table>
            {!!data.length && (
                <div className="data-table-footer" data-testid="dataTableFooter">
                    <div className="footer-section">
                        <div className="footer-label">Results Per Page</div>
                        <Form.Select
                            id="resultsPerPage"
                            className="page-size"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                            defaultValue={10}
                            data-testid="resultsPerPageSelect"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            {shouldShowLargePageSizes && (
                                <>
                                    <option value={50}>50</option>
                                    <option value={75}>75</option>
                                    <option value={100}>100</option>
                                </>
                            )}
                        </Form.Select>
                    </div>
                    <div className="footer-section">
                        <div className="footer-label">
                            Page {paginationState.pageIndex + 1} of {table.getPageCount()}
                        </div>
                        <Pagination>
                            <Pagination.Prev
                                onClick={() => {
                                    table.previousPage();
                                }}
                                disabled={!table.getCanPreviousPage()}
                                data-testid="prevPageButton"
                            >
                                <ChevronLeft size={24} />
                            </Pagination.Prev>
                            {paginationButtons}
                            <Pagination.Next
                                onClick={() => {
                                    table.nextPage();
                                }}
                                disabled={!table.getCanNextPage()}
                                data-testid="nextPageButton"
                            >
                                <ChevronRight size={24} />
                            </Pagination.Next>
                        </Pagination>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
